<template>
  <div class="vehicle-section">
    <h5 class="vehicle-section__title">
      {{ $t('fleetTaskInstant.title.vehicleList') }}
    </h5>

    <template v-if="shouldShowVehicleList">
      <div class="vehicle-section__search-container">
        <b-input-group size="sm" class="__search-input-group" @click="focusSearch">
          <template #prepend>
            <i class="ps-2 mc-icon icon-fill_search"></i>
          </template>
          <b-form-input
            ref="searchInput"
            type="search"
            :placeholder="$t('fleetTaskInstant.placeholder.searchVehicleOrChooseMap')"
            :value="search"
            @input="handleSearchChange"
          />
        </b-input-group>
      </div>

      <template v-if="isLoadingGlobal || isLoading">
        <div class="px-2 py-2 d-flex gap-3 flex-column">
          <b-skeleton v-for="(i, idx) in ['100', '100']" :key="idx" :width="i" />
        </div>
      </template>

      <div v-else-if="vehicles.length > 0" ref="vehicle-list-ref" class="vehicle-section__group">
        <div
          v-for="(vehicle, index) in vehicles"
          :key="`vehicle-${vehicle.id}`"
          class="vehicle-section__list"
        >
          <div class="vehicle-section__detail">
            <div class="__header">
              <div>
                <div class="d-flex gap-2 align-items-center">
                  <h5 class="__title">
                    {{ vehicle.license_plate }}
                  </h5>
                  <McBadge
                    v-if="vehicle?.fleet_task"
                    type="button"
                    variant="outlineWarning"
                    :text="$t('fleetTaskInstant.option.onAssignment')"
                  />
                </div>
                <div class="__vehicle-type">
                  {{ vehicle.vehicle_type?.type }}
                </div>
              </div>

              <McButton
                size="sm"
                :text="$t('fleetTaskInstant.action.assign')"
                :disabled="isLoadingGlobal"
                @click="handleConfirmFleetModal(vehicle, index)"
              />
            </div>
            <div class="__body">
              <div class="__other-infos">
                <div class="__distance">
                  <i :id="`popover-calculate-${vehicle.id}`" class="icon-outline_calculate" />
                  <b-popover
                    triggers="hover"
                    placement="lefttop"
                    :target="`popover-calculate-${vehicle.id}`"
                  >
                    <div class="popover-text-wrapper">
                      {{ tooltipDistance }}
                    </div>
                  </b-popover>
                  <div class="__text">
                    {{ formatDistance(vehicle.distance) }}
                  </div>
                </div>
                <template v-if="vehicle.fuel != '-' && vehicle.fuel != null">
                  <div class="__divider" />
                  <div class="__fuel">
                    <i class="icon-outline_fuel" />
                    <div class="__text">
                      {{ vehicle.fuel }}
                    </div>
                  </div>
                </template>
                <template v-if="vehicle?.fleet_task">
                  <div class="__divider" />
                  <template v-if="hasUserGroupFeature && !isSuperUser()"> - </template>
                  <template v-else>
                    <McButton
                      :id="`tooltip-info-fleet-${vehicle.fleet_task?.number}`"
                      size="sm"
                      variant="minimal"
                      iconPosition="right"
                      icon="icon-outline_external-link"
                      class="custom-button"
                      :text="vehicle.fleet_task?.number"
                      :disabled="isLoadingGlobal"
                      @click="
                        handleOpenFleetDetail(
                          $event,
                          vehicle.fleet_task.id,
                          vehicle.fleet_task.type
                        )
                      "
                    />
                    <FleetOrderTooltip
                      :key="`tooltip-${vehicle.fleet_task?.number}`"
                      :fleetNumber="vehicle.fleet_task?.number"
                      :driver="vehicle.fleet_task.driver?.name"
                      :estimatedArrival="vehicle.fleet_task?.estimated_arrival_on"
                    />
                  </template>
                </template>
              </div>
            </div>
          </div>
        </div>

        <ConfirmFleetModal
          :vehicle="vehicles[selectedVehicleIdx]"
          @create-quick-assignment="handleAssignVehicle"
        />
      </div>
    </template>
    <template v-else>
      <div class="empty-table-container">
        <img :src="illustrationPath" alt />
        <p>{{ $t('fleetTaskInstant.text.userGroupNotDefined') }}</p>
        <McButton
          variant="secondary"
          :text="`${$t('global.form.select')} ${$t('global.field.userGroup')}`"
          @click="handleOpenUserGroupModal"
        />
      </div>
    </template>
  </div>
</template>

<script>
import { mapState, mapGetters } from 'vuex'
import { Helper } from '@/mixin/helper'
import { debounce } from '@/helpers/debounce'
import { formatNumber } from '@/helpers/format-number'
import McButton from '@/components/McButton'
import McBadge from '@/components/McBadge'

import FleetOrderTooltip from '../../components/FleetOrderTooltip'
import ConfirmFleetModal from '../../components/ConfirmFleetModal'

import * as NearestVehicle from '@/services/rest-api/lib/fleet-task-instant/nearest-vehicle'
import { EventBus } from '../../lib/event-bus.js'

export default {
  components: {
    McButton,
    McBadge,
    FleetOrderTooltip,
    ConfirmFleetModal,
  },
  mixins: [Helper],
  data() {
    return {
      isLoading: false,
      search: '',
      selectedVehicleIdx: null,
      vehicles: [],
    }
  },
  computed: {
    ...mapState('fleetTaskInstant', ['selectedPoints', 'isLoadingGlobal', 'formValues']),
    ...mapGetters({
      isWhitelabel: 'identity/isWhitelabel',
      isMetrack: 'identity/isMetrack',
      hasAccessAll: 'identity/hasAccessAll',
      getFeatureFlagValue: 'identity/getFeatureFlagValue',
    }),
    illustrationPath() {
      if (this.isWhitelabel() || this.isMetrack()) {
        return require(`@/assets/images/illustration/whitelabel/no-data.svg`)
      }
      return require(`@/assets/images/illustration/saas/no-data.svg`)
    },
    hasUserGroupFeature() {
      const hasScope = this.hasAccessAll(['tms-user-group:read'])

      return hasScope
    },
    tooltipDistance() {
      return this.$t('fleetTaskInstant.tooltip.distance')
    },
    shouldShowVehicleList() {
      return (
        !(this.hasUserGroupFeature && this.isSuperUser()) || this.formValues.user_group_id !== null
      )
    },
  },
  watch: {
    isLoadingGlobal: {
      immediate: true,
      handler(newVal) {
        if (!newVal && this.shouldShowVehicleList) {
          this.getData()
        }
      },
    },
  },
  created() {
    EventBus.$on('get-nearest-vehicle', this.getData)
  },
  beforeDestroy() {
    EventBus.$off('get-nearest-vehicle', this.getData)
  },
  methods: {
    focusSearch() {
      this.$refs.searchInput.focus()
    },
    handleOpenUserGroupModal() {
      EventBus.$emit('open-user-group-modal')
    },
    formatDistance(value) {
      let distance = value
      if (distance >= 1000) {
        return formatNumber(distance / 1000) + ' Km'
      }
      return '< 1 Km'
    },

    handleOpenFleetDetail(event, id, type) {
      event.currentTarget.blur()
      let route
      switch (type) {
        case 'FLEET_TASK_INSTANT':
          route = this.$router.resolve({ name: 'Fleet Task Instant Detail', params: { id } })
          break
        case 'FLEET_TASK':
          route = this.$router.resolve({ name: 'Fleet Task Detail', params: { id } })
          break
      }
      window.open(route.href, '_blank')
    },

    async getData() {
      try {
        if (this.isLoading) return

        this.isLoading = true

        const firstPoint = this.selectedPoints[0]

        let filter = {
          latitude: firstPoint.lat,
          longitude: firstPoint.lng,
          search: this.search,
        }

        if (
          this.hasUserGroupFeature &&
          this.isSuperUser() &&
          this.formValues.user_group_id !== null
        ) {
          filter = {
            ...filter,
            user_group_id: this.formValues.user_group_id,
          }
        }

        const response = await NearestVehicle.get(filter)

        if (!response?.data?.result) {
          this.$mcToast.error(this.$t('global.somethingWentWrong'))
          return
        }

        const objVehicles = {}
        response.data.result.forEach((vehicle) => {
          this.$set(objVehicles, vehicle.id, {
            ...vehicle,
          })
        })

        this.vehicles = response.data.result
        this.$store.dispatch('fleetTaskInstant/setListVehicle', objVehicles)
      } finally {
        this.isLoading = false
      }
    },

    handleSearchChange: debounce(async function (_search) {
      this.search = _search

      await this.getData()
    }, 500),

    handleConfirmFleetModal(vehicle, idx) {
      const isSkipFleetConfirmation = localStorage.getItem('isSkipFleetConfirmation') ? false : true

      if (isSkipFleetConfirmation && vehicle.fleet_task) {
        this.selectedVehicleIdx = idx
        this.$bvModal.show('confirm-fleet-modal')
        return
      }

      this.handleAssignVehicle(vehicle)
    },

    handleAssignVehicle(vehicle) {
      EventBus.$emit('create-fleet-task-instant', vehicle)
    },
  },
}
</script>

<style lang="scss" scoped>
$total-height: calc(100vh - 150px);

.vehicle-section {
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 16px;
  padding-top: 0;
  height: calc($total-height / 2);

  .__search-input-group {
    border-width: $input-border-width;
    border-color: $input-border-color;
    border-radius: 4px;
    border-style: solid;
    margin-left: 0;

    border-radius: 4px;

    // if search is disabled
    &:has(input:disabled) {
      background: $input-disabled-bg;
    }

    .clear-search-btn {
      border: none;
      background-color: transparent;
      padding: 0px;
      margin: 0px;
      cursor: pointer;
      display: flex;
      justify-content: center;
      align-items: center;
    }

    .clear-search-btn:disabled {
      cursor: default;
    }

    input {
      border: none;
      height: 37px;
    }

    .input-group-prepend,
    .input-group-append {
      border: none;
      display: flex;
      align-items: center;

      .mc-icon:before {
        font-size: 16px;
        color: $input-placeholder-color;
      }
    }
  }

  &__title {
    margin: 0;
  }

  &__group {
    display: flex;
    flex-direction: column;
    overflow-y: auto;

    border-radius: 8px;
    border: 1px solid $teal-100;
  }

  &__list {
    border-radius: 4px;
    border-bottom: 1px solid $teal-100;

    &:last-child {
      border-bottom: none;
    }

    .__header {
      display: flex;
      justify-content: space-between;
      padding-bottom: 8px;

      .__title {
        font-size: 13px;
        font-weight: 500;
        margin-bottom: 0px;
      }
    }

    .__body {
      display: flex;
      flex-direction: column;
      font-size: 16px;
      margin: 8px 0 0 0;
      gap: 10px;
    }
  }

  &__detail {
    padding: 12px;
    .__title {
      font-size: 16px;
    }

    .current-address-wrapper {
      display: flex;
      gap: 6px;
      align-items: center;

      .__current-address {
        font-weight: 400;
        font-size: 12px;

        overflow: hidden;
        text-overflow: ellipsis;
        display: -webkit-box;
        -webkit-line-clamp: 2;
        line-clamp: 2;
        -webkit-box-orient: vertical;
      }

      .__address {
        font-weight: 400;
        color: $mep-color-text-300;
        font-size: 12px;

        overflow: hidden;
        text-overflow: ellipsis;
        display: -webkit-box;
        -webkit-line-clamp: 2;
        line-clamp: 2;
        -webkit-box-orient: vertical;
      }
    }

    .__vehicle-type {
      font-size: 11px;
    }

    .__other-infos,
    .__distance,
    .__fuel {
      display: flex;
      gap: 4px;
      font-size: 16px;
      align-items: center;

      .__text {
        font-size: 12px;
      }
    }

    .__vehicle-type,
    .__distance,
    .__fuel,
    .__divider,
    .__distance > i:before,
    .__fuel > i:before {
      color: $mep-color-text-300;
    }

    .__divider {
      height: 4px;
      width: 4px;
      background-color: $mep-color-text-300;
      border-radius: 4px;
    }
  }

  &__banner {
    padding: 6px 12px;
    background-color: $yellow-100;
    display: flex;
    justify-content: space-between;
    align-items: center;

    .__label {
      font-size: 12px;
      font-weight: 600;
      color: $yellow;
    }

    .__action-item {
      display: flex;
      align-items: center;

      .mc-fa {
        font-size: 16px;
      }

      .vertical-line {
        margin: 0;
        width: 15px;
        transform: rotate(90deg);
      }
    }
  }
}

.popover-text-wrapper {
  font-family: $font-family-sans-serif;
  font-size: 12px;
  background-color: $mep-color-text-400 !important;
  color: $white !important;
  width: 200px !important;
  padding: 6px 12px;
}
</style>
